if (!customElements.get('slider-centered-component')) {
  customElements.define(
    'slider-centered-component',
    class SliderCenteredComponent extends HTMLElement {
      constructor() {
        super();

        this.container = this.querySelector('.centered-slider');
        this.track = this.querySelector('.centered-slider__track');
        this.slides = this.querySelectorAll('.centered-slider__item');
        this.prevButton = this.querySelectorAll('button[name="previous"]');
        this.nextButton = this.querySelectorAll('button[name="next"]');

        this.currentIndex = 0;
        this.centeredSlides = true;
        this.totalSlides = this.slides.length;
        this.clonedSlides = false;

        this.slidesPerView = Number(this.dataset.slidesPerView);
        this.spaceBetween = Number(this.dataset.spaceBetween);
        if (window.innerWidth < 750) {
          this.slidesPerView = 2;
          this.spaceBetween = this.spaceBetween / 2;
        }

        this.isAnimating = false;
        
        // Drag/Touch properties
        this.isDragging = false;
        this.startX = 0;
        this.currentX = 0;
        this.startTransform = 0;
        this.dragThreshold = 50;
        this.velocity = 0;
        this.lastMoveTime = 0;
        this.lastMoveX = 0;
      }

      connectedCallback() {
        if (this.totalSlides <= 1) return;
        
        this.cloneSlides();
        this.setTrackWidth();
        this.goToSlide(this.slidesPerView, false);

        this.prevButton.forEach(button =>
          button.addEventListener('click', () => this.handleSlideChange(this.currentIndex - 1))
        );
        this.nextButton.forEach(button =>
          button.addEventListener('click', () => this.handleSlideChange(this.currentIndex + 1))
        );

        window.addEventListener('resize', this.updateSlidesOnResize.bind(this));

        this.track.addEventListener('transitionend', () => {
          this.isAnimating = false;
          if (this.currentIndex === 0) {
            this.goToSlide(this.totalSlides, false);
          } else if (this.currentIndex === this.totalSlides + this.slidesPerView) {
            this.goToSlide(this.slidesPerView, false);
          }
        });

        // Add drag and touch event listeners
        this.addDragListeners();
        this.addTouchListeners();
      }

      addDragListeners() {
        // Mouse events
        this.container.addEventListener('mousedown', this.onDragStart.bind(this));
        document.addEventListener('mousemove', this.onDragMove.bind(this));
        document.addEventListener('mouseup', this.onDragEnd.bind(this));
        
        // Prevent default drag behavior on images
        this.container.addEventListener('dragstart', (e) => e.preventDefault());
      }

      addTouchListeners() {
        // Touch events
        this.container.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.container.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        this.container.addEventListener('touchend', this.onTouchEnd.bind(this));
      }

      onDragStart(e) {
        if (this.isAnimating) return;
        
        this.isDragging = true;
        this.startX = e.clientX;
        this.startTransform = this.getCurrentTransform();
        this.lastMoveTime = Date.now();
        this.lastMoveX = e.clientX;
        
        this.track.classList.add('dragging');
        this.container.style.cursor = 'grabbing';
      }

      onDragMove(e) {
        if (!this.isDragging) return;
        
        e.preventDefault();
        this.currentX = e.clientX;
        const deltaX = this.currentX - this.startX;
        
        // Calculate velocity
        const now = Date.now();
        const timeDelta = now - this.lastMoveTime;
        if (timeDelta > 0) {
          this.velocity = (e.clientX - this.lastMoveX) / timeDelta;
        }
        this.lastMoveTime = now;
        this.lastMoveX = e.clientX;
        
        // Apply transform with resistance at edges
        const newTransform = this.startTransform + deltaX * 0.8;
        this.track.style.transform = `translateX(${newTransform}px)`;
      }

      onDragEnd(e) {
        if (!this.isDragging) return;
        
        this.isDragging = false;
        this.track.classList.remove('dragging');
        this.container.style.cursor = 'grab';
        
        const deltaX = this.currentX - this.startX;
        const absVelocity = Math.abs(this.velocity);
        
        // Determine slide direction based on drag distance and velocity
        if (Math.abs(deltaX) > this.dragThreshold || absVelocity > 0.5) {
          if (deltaX > 0 || this.velocity > 0.5) {
            this.handleSlideChange(this.currentIndex - 1);
          } else if (deltaX < 0 || this.velocity < -0.5) {
            this.handleSlideChange(this.currentIndex + 1);
          } else {
            this.goToSlide(this.currentIndex);
          }
        } else {
          this.goToSlide(this.currentIndex);
        }
        
        this.velocity = 0;
      }

      onTouchStart(e) {
        if (this.isAnimating) return;
        
        this.isDragging = true;
        this.startX = e.touches[0].clientX;
        this.startTransform = this.getCurrentTransform();
        this.lastMoveTime = Date.now();
        this.lastMoveX = e.touches[0].clientX;
        
        this.track.classList.add('dragging');
      }

      onTouchMove(e) {
        if (!this.isDragging) return;
        
        // Prevent page scrolling when swiping horizontally
        const touch = e.touches[0];
        const deltaX = touch.clientX - this.startX;
        const deltaY = touch.clientY - (this.startY || touch.clientY);
        
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          e.preventDefault();
        }
        
        this.currentX = touch.clientX;
        
        // Calculate velocity
        const now = Date.now();
        const timeDelta = now - this.lastMoveTime;
        if (timeDelta > 0) {
          this.velocity = (touch.clientX - this.lastMoveX) / timeDelta;
        }
        this.lastMoveTime = now;
        this.lastMoveX = touch.clientX;
        
        // Apply transform with resistance
        const newTransform = this.startTransform + deltaX * 0.8;
        this.track.style.transform = `translateX(${newTransform}px)`;
      }

      onTouchEnd(e) {
        if (!this.isDragging) return;
        
        this.isDragging = false;
        this.track.classList.remove('dragging');
        
        const deltaX = this.currentX - this.startX;
        const absVelocity = Math.abs(this.velocity);
        
        // Determine slide direction
        if (Math.abs(deltaX) > this.dragThreshold || absVelocity > 0.3) {
          if (deltaX > 0 || this.velocity > 0.3) {
            this.handleSlideChange(this.currentIndex - 1);
          } else if (deltaX < 0 || this.velocity < -0.3) {
            this.handleSlideChange(this.currentIndex + 1);
          } else {
            this.goToSlide(this.currentIndex);
          }
        } else {
          this.goToSlide(this.currentIndex);
        }
        
        this.velocity = 0;
      }

      getCurrentTransform() {
        const style = window.getComputedStyle(this.track);
        const matrix = style.transform;
        
        if (matrix === 'none') return 0;
        
        const values = matrix.split('(')[1].split(')')[0].split(',');
        return parseFloat(values[4]) || 0;
      }

      updateSlidesOnResize() {
        const newSlidesPerView = window.innerWidth < 750 ? 2 : Number(this.dataset.slidesPerView);
        const newSpaceBetween = window.innerWidth < 750
          ? Number(this.dataset.spaceBetween) / 2
          : Number(this.dataset.spaceBetween);
      
        if (newSlidesPerView !== this.slidesPerView || newSpaceBetween !== this.spaceBetween) {
          this.slidesPerView = newSlidesPerView;
          this.spaceBetween = newSpaceBetween;
      
          this.setTrackWidth();
          this.goToSlide(this.slidesPerView, false);
        }
      }

      handleSlideChange(targetIndex) {
        if (this.isAnimating || this.isDragging) return;
        this.isAnimating = true;
        this.goToSlide(targetIndex);
      }

      cloneSlides() {
        const firstSlides = Array.from(this.slides).slice(0, this.slidesPerView).map(slide => slide.cloneNode(true));
        const lastSlides = Array.from(this.slides).slice(-this.slidesPerView).map(slide => slide.cloneNode(true));

        firstSlides.forEach(slide => this.track.appendChild(slide));
        lastSlides.forEach(slide => this.track.insertBefore(slide, this.slides[0]));

        this.slides = this.track.querySelectorAll('.centered-slider__item');
        this.clonedSlides = true;
      }

      setTrackWidth() {
        const slideWidth = this.container.clientWidth / this.slidesPerView - this.spaceBetween;
        const totalWidth = (slideWidth + this.spaceBetween) * this.slides.length;
        this.track.style.width = `${totalWidth}px`;

        this.slides.forEach(slide => {
          slide.style.flex = `0 0 ${slideWidth}px`;
        });
      }

      goToSlide(index, transition = true) {
        if (!transition) {
          this.track.style.transition = 'none';
        } else {
          this.track.style.transition = 'transform 0.3s ease-in-out';
        }

        this.currentIndex = index;
        const offset = this.centeredSlides
          ? this.getCenteredOffset(index)
          : -(index * (this.slides[0].clientWidth + this.spaceBetween));

        this.track.style.transform = `translateX(${offset}px)`;

        if (!transition) {
          setTimeout(() => (this.track.style.transition = 'transform 0.3s ease-in-out'), 50);
        }
      }

      getCenteredOffset(index) {
        const visibleWidth = this.container.clientWidth;
        const slideWidth = this.slides[0].clientWidth;
        const totalOffset = index * (slideWidth + this.spaceBetween);
        const centeredOffset = totalOffset - (visibleWidth / 2 - slideWidth / 2);
        return -centeredOffset;
      }
    }
  );
}